import { useEffect, useState, useCallback, useMemo } from "react";
import { RefreshCw, Wrench, Wallet, Server, Calendar } from "lucide-react";
import { toast } from "sonner";
import SummaryCards from "./SummaryCards";
import { crmList } from "@/lib/api";
import { INR, fmtDate } from "./crmUtils";

const MAINT_STATUSES = new Set(["Maintenance", "Maintainence"]);

/** Compute the renewal date (deployed_live_on + 1 year). Returns "" if no deploy date. */
function _renewalDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(+d)) return "";
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  } catch { return ""; }
}

function _daysToRenewal(iso) {
  const r = _renewalDate(iso);
  if (!r) return null;
  const rd = new Date(r);
  const now = new Date();
  return Math.round((rd - now) / (24 * 3600 * 1000));
}

export default function MaintenanceProjects({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const all = await crmList(token, "projects", {});
      setRows(all.filter((p) => MAINT_STATUSES.has(p.website_status)));
    } catch {
      toast.error("Couldn't load maintenance projects");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  const summary = useMemo(() => {
    const now = Date.now();
    const totalAmc = rows.reduce((s, r) => s + (Number(r.amc_amount) || 0), 0);
    const dueSoon = rows.filter((r) => {
      const days = _daysToRenewal(r.deployed_live_on);
      return days !== null && days >= 0 && days <= 30;
    }).length;
    const overdue = rows.filter((r) => {
      const days = _daysToRenewal(r.deployed_live_on);
      return days !== null && days < 0;
    }).length;
    // active clients
    const clients = new Set(rows.map((r) => r.client).filter(Boolean));
    return { totalAmc, dueSoon, overdue, activeClients: clients.size };
  }, [rows]);

  const cards = [
    { label: "Maintenance projects", value: rows.length,                          icon: <Wrench className="h-3.5 w-3.5" />, tone: "bg-amber-500" },
    { label: "Total AMC (annual)",   value: INR(summary.totalAmc),                icon: <Wallet className="h-3.5 w-3.5" />, tone: "bg-emerald-500", accent: "text-emerald-700" },
    { label: "Active clients",       value: summary.activeClients,                icon: <Server className="h-3.5 w-3.5" />, tone: "bg-blue-500" },
    { label: "Renewals in 30 days",  value: summary.dueSoon,                      icon: <Calendar className="h-3.5 w-3.5" />, tone: "bg-orange-500" },
    { label: "Overdue renewals",     value: summary.overdue,                      icon: <Calendar className="h-3.5 w-3.5" />, tone: "bg-rose-500", accent: summary.overdue > 0 ? "text-rose-700" : "" },
  ];

  return (
    <div data-testid="crm-maintenance-page">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">Maintenance</div>
          <h2 className="mt-1 crm-h text-3xl md:text-4xl text-[#0a0a0a]">
            AMC snapshot
          </h2>
          <p className="mt-2 text-sm text-[#4a4a4a]">
            Every project whose website status is <b>Maintenance</b> shows up here. Renewal date is <b>Deployed on + 1 year</b>.
            Edit any project to update the deploy date or AMC amount.
          </p>
        </div>
        <button onClick={reload} data-testid="crm-maint-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 text-xs border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722] transition-colors">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <SummaryCards cards={cards} />

      <div className="bg-white border border-black/10 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-sm text-[#4a4a4a]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-sm text-[#4a4a4a]">
            No projects marked <b>Maintenance</b> yet. Set a project&rsquo;s website status to <b>Maintenance</b> and it will appear here automatically.
          </div>
        ) : (
          <table className="w-full text-left min-w-[1000px]">
            <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
              <tr>
                <th className="px-4 py-4 w-12">S.no</th>
                <th className="px-4 py-4">Project</th>
                <th className="px-4 py-4">Client</th>
                <th className="px-4 py-4">Hosting server</th>
                <th className="px-4 py-4">Deployed to LIVE</th>
                <th className="px-4 py-4">Renewal on</th>
                <th className="px-4 py-4 text-right">AMC amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const days = _daysToRenewal(r.deployed_live_on);
                let renewalCls = "text-[#4a4a4a]";
                let renewalLabel = "";
                if (days !== null) {
                  if (days < 0) { renewalCls = "text-rose-700 font-bold"; renewalLabel = `${Math.abs(days)}d overdue`; }
                  else if (days <= 30) { renewalCls = "text-orange-700 font-bold"; renewalLabel = `in ${days}d`; }
                  else if (days <= 90) { renewalCls = "text-amber-700"; renewalLabel = `in ${days}d`; }
                  else { renewalCls = "text-emerald-700"; renewalLabel = `in ${days}d`; }
                }
                return (
                  <tr key={r.id} data-testid={`crm-maint-row-${r.id}`}
                      className="border-b border-black/5 hover:bg-[#0a0a0a]/[0.02] transition-colors">
                    <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{String(i + 1).padStart(2, "0")}</td>
                    <td className="px-4 py-4 font-semibold text-[#0a0a0a]">{r.name}</td>
                    <td className="px-4 py-4 text-sm">{r.client || "—"}</td>
                    <td className="px-4 py-4">
                      {r.hosting_server
                        ? <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] bg-zinc-100 rounded">{r.hosting_server}</span>
                        : "—"}
                    </td>
                    <td className="px-4 py-4 text-xs font-mono">{fmtDate(r.deployed_live_on) || <span className="text-[#9a9a9a]">not set</span>}</td>
                    <td className="px-4 py-4 text-xs font-mono">
                      <div>{fmtDate(_renewalDate(r.deployed_live_on)) || "—"}</div>
                      {renewalLabel && <div className={`text-[10px] uppercase tracking-[0.1em] ${renewalCls}`}>{renewalLabel}</div>}
                    </td>
                    <td className="px-4 py-4 text-right font-mono font-bold text-emerald-700">{INR(r.amc_amount || 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
